import { KeyValue } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  effect,
  forwardRef,
  Injector,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  inject,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { WidgetConfigAccessor } from '../services/widget-config.accessor';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { TypographyKeys } from '@finxone-platform/shared/sys-config-types';
import { Subject, takeUntil } from 'rxjs';
import { InputValidationFactory } from '../input-field-validation/input-validations-factory';
import { ValidationConfig } from '../input-field-validation/validation-config.type';
import { renderOptionsWithHandlebars } from '../input-options/input-options.utils';

@Component({
  standalone: false,
  selector: 'finxone-radio',
  templateUrl: './finxone-radio.component.html',
  styleUrls: ['./finxone-radio.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FinxoneRadioComponent),
      multi: true,
    },
  ],
})
export class FinxoneRadioComponent
  extends InputValidationFactory
  implements ControlValueAccessor, OnInit, OnChanges
{
  private readonly cd = inject(ChangeDetectorRef);
  private readonly configService = inject(WidgetConfigAccessor);
  private readonly injector = inject(Injector);

  public control = new FormControl();
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @Input() options: any[] = []; // List of options
  @Input() label = '';
  @Input() readOnly = false;
  @Input() isDisabled = false;
  @Input() formControlInvalid = false;
  @Input() globalCssVariables: Record<string, string> = {};
  @Input() customRadioOptionTemplate?: TemplateRef<any>; // Custom radio option template
  @Input() customErrorTemplate?: TemplateRef<any>;
  @Input() id = '';
  @Input() name = '';
  @Input() value = '';
  @Input() validationConfig: ValidationConfig[] = [];
  @Input() showErrors = true;
  @Input() optionLabelKey = 'title';
  @Input() optionValueKey = 'key';
  @Input() inputLabelTooltipText: string;
  @Input() helperText: string;
  @Input() parentControl?: FormControl;
  @Input() radioCheckboxOptionsLayout = 'vertical';
  // eslint-disable-next-line @typescript-eslint/ban-types
  onChange: Function = () => {};
  // eslint-disable-next-line @typescript-eslint/ban-types
  onTouched: Function = () => {};

  public textStylingClass: TypographyKeys;
  private cssVars: Record<string, string> = {};
  public isRadioGroupOnFocus = false;
  public renderedOptionLabels: Record<string, string> = {};
  public tooltipBorderRadiusClass = '';
  public tooltipTextStyleClass = '';

  constructor() {
    super();
    effect(() => {
      const globalCssVariables = this.configService.globalCssVariables();
      if (globalCssVariables) {
        this.cssVars = globalCssVariables;
        this.textStylingClass = this.getCssVariableValue('--inputFieldTextStylingClass') as TypographyKeys;
        this.tooltipTextStyleClass = this.getCssVariableValue('--tooltipTextStyleClass');
        this.tooltipBorderRadiusClass = this.getCssVariableValue('--tooltipBorderRadiusClass');
      }
    });
  }

  ngOnInit() {
    if (this.parentControl) this.control = this.parentControl;
    this.control.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cd.markForCheck();
    });
    this.renderAllOptionLabels();
  }

  private renderAllOptionLabels(): void {
    renderOptionsWithHandlebars(this.options, this.optionLabelKey, this.optionValueKey, this.injector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.renderedOptionLabels = data?.renderedOptions;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']?.currentValue !== undefined) {
      this.control.setValue(changes['value']?.currentValue);
    }
    if (changes['validationConfig']?.currentValue !== undefined) {
      const validators = this.setupValidation(this.validationConfig);
      this.control.setValidators(validators);
      this.control.updateValueAndValidity();
    }
    if (changes['formControlInvalid']?.currentValue !== undefined) {
      this.setFormControlValidity(this.formControlInvalid);
    }
  }

  private setFormControlValidity(formControlInvalid: boolean) {
    if (!formControlInvalid) {
      this.control.markAsPristine();
      this.control.markAsUntouched();
    } else {
      this.control.markAsDirty();
      this.control.markAsTouched();
    }
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    isDisabled ? this.control.disable() : this.control.enable();
  }

  public onRadioValueChange(value: string) {
    this.value = value;
    this.onChange(value);
    this.cd.markForCheck();
  }

  private getCssVariableValue(variableName: string): string {
    return this.cssVars[variableName];
  }

  public getErrorMessageBasedOnKey(error: KeyValue<string, any>): string {
    const key = error.key;
    if (!this.validationConfig.some((config) => config.type === key) && typeof error.value === 'string') {
      return error.value;
    } else {
      const matchingConfig = this.validationConfig.find((config) => config.type === key);
      if (matchingConfig) {
        return this.getErrorMessage(matchingConfig);
      }
    }
    return 'Cannot find error message';
  }
}
